import {
  useEffect,
  useRef,
  useState,
  type InputHTMLAttributes,
  type KeyboardEvent,
} from "react";
import { Eye, EyeOff } from "lucide-react";
import { cn } from "@/utils/format";

type PasswordInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, "type">;

export function PasswordInput({ className, ...props }: PasswordInputProps) {
  const [visible, setVisible] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const toggledRef = useRef(false);
  const selectionRef = useRef<{ start: number | null; end: number | null }>({
    start: null,
    end: null,
  });

  const toggle = () => {
    const el = inputRef.current;
    if (el) {
      selectionRef.current = {
        start: el.selectionStart,
        end: el.selectionEnd,
      };
    }
    toggledRef.current = true;
    setVisible((v) => !v);
  };

  useEffect(() => {
    if (!toggledRef.current) return;
    const el = inputRef.current;
    if (!el) return;
    const { start, end } = selectionRef.current;
    requestAnimationFrame(() => {
      el.focus();
      try {
        if (start != null && end != null) {
          el.setSelectionRange(start, end);
        }
      } catch {
        // Ignore selection errors for edge browsers/types.
      }
    });
  }, [visible]);

  const onToggleKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      toggle();
    }
  };

  return (
    <div className="password-input">
      <input
        {...props}
        ref={inputRef}
        type={visible ? "text" : "password"}
        className={cn("input password-input__field", className)}
      />
      <button
        type="button"
        className="password-input__toggle"
        onClick={toggle}
        onKeyDown={onToggleKeyDown}
        aria-label={visible ? "Hide password" : "Show password"}
        aria-pressed={visible}
        title={visible ? "Hide password" : "Show password"}
        tabIndex={0}
      >
        <span className="password-input__icon" data-visible={visible ? "true" : "false"}>
          {visible ? <EyeOff size={16} strokeWidth={2} /> : <Eye size={16} strokeWidth={2} />}
        </span>
      </button>
    </div>
  );
}
